import React, { useEffect, useMemo, useState } from 'react';
import { botSessionKeyApi } from '../services/api';
import './BotSessionKeysPanel.css';

const PERMISSION_OPTIONS = [
  ['own_profile_read', 'Own profile read'],
  ['own_profile_write', 'Own profile write'],
  ['bookings_read', 'Bookings read'],
  ['bookings_write', 'Bookings write'],
  ['organisations_read', 'Organisations read'],
  ['organisations_write', 'Organisations write'],
  ['organisation_users_read', 'Organisation users read'],
  ['organisation_users_write', 'Organisation users write'],
  ['rooms_read', 'Rooms read'],
  ['rooms_write', 'Rooms write'],
  ['openclaw_events_read', 'OpenClaw events read'],
];

// M004-F0763 / M001-D013 §8.2.2: the server assigns the default window when
// expires_at is omitted; the UI only offers bounded choices (max 365 days).
// M001-D009 §9.5.4: the event-type allowlist may hold only NotificationType values.
// Source list: Core src/services/notification.service.ts:13-44 (origin/main f5904d3).
// 2sv-challenge is never offered (§9.5.4 and §9.5.5; it is a push category, not a row
// type, and its rows are never delivered to a bot). Hard-coded here; keep it in sync with Core.
const NOTIFICATION_EVENT_TYPES = [
  'info',
  'warning',
  'success',
  'error',
  'booking_request',
  'booking_approved',
  'booking_rejected',
  'room_hire_enquiry',
  'document_received',
  'user_onboarding',
  'document_expiring',
  'system',
  'network_new_device',
  'network_device_offline',
  'network_arp_spoof',
  'network_arp_flood',
  'network_ip_conflict',
  'network_port_scan',
  'network_ids_alert',
  'network_truenas_alert',
  'network_unifi_offline',
  'network_ha_offline',
  'network_camera_offline',
  'network_tuya_offline',
  'network_high_latency',
  'panoptic_watch_rule',
  'ledger_dispatch_awaiting_approval',
  'ledger_billing_chain_finding',
];

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_EXPIRY_DAYS = 90;
const EXPIRY_CHOICES = [7, 30, 60, 90, 180, 365];
// M001-F0271: the same 30-day notice window the production smoke gate uses.
const EXPIRY_WARNING_DAYS = 30;

const EMPTY_PERMISSIONS = PERMISSION_OPTIONS.reduce((permissions, [key]) => {
  permissions[key] = key === 'own_profile_read';
  return permissions;
}, {});

const PRODUCTION_READ_ONLY_PERMISSIONS = {
  ...EMPTY_PERMISSIONS,
  bookings_read: true,
  bookings_write: false,
  organisations_read: true,
  organisations_write: false,
  organisation_users_read: true,
  organisation_users_write: false,
  rooms_read: false,
  rooms_write: false,
  own_profile_write: false,
};

const emptyForm = () => ({
  label: '',
  maxSal: '3',
  expiryDays: String(DEFAULT_EXPIRY_DAYS),
  permissions: { ...EMPTY_PERMISSIONS },
  // M004-F0754: read is off and the allowlist is empty (delivers nothing) by default.
  notificationsRead: false,
  notificationsWrite: false,
  notificationsEventTypes: [],
});

const getPayload = (response) => response.data?.data || response.data || {};

const getErrorMessage = (err, fallback) => (
  err.response?.data?.error?.message
  || err.response?.data?.message
  || err.response?.data?.error
  || err.message
  || fallback
);

const formatDateTime = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Server expiresAt is the only source of truth; createdAt is never used to infer it.
// Warning iff the displayed whole-day count is <= EXPIRY_WARNING_DAYS.
const describeExpiry = (value, now = Date.now()) => {
  const expiresAt = value ? new Date(value) : null;
  if (!expiresAt || Number.isNaN(expiresAt.getTime())) {
    return { tone: 'unknown', text: 'Expiry unknown' };
  }
  const dateText = expiresAt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const remainingMs = expiresAt.getTime() - now;
  if (remainingMs <= 0) {
    return { tone: 'expired', text: `Expired on ${dateText}` };
  }
  const days = Math.floor(remainingMs / DAY_MS);
  const daysText = days === 0 ? 'less than a day left' : `${days} ${days === 1 ? 'day' : 'days'} left`;
  return {
    tone: days <= EXPIRY_WARNING_DAYS ? 'warning' : 'ok',
    text: `Expires ${dateText} · ${daysText}`,
  };
};

const formatFingerprint = (keyId = '') => {
  if (!keyId) return 'Unknown';
  if (keyId.length <= 14) return keyId;
  return `${keyId.slice(0, 8)}...${keyId.slice(-6)}`;
};

const normalizeEventTypes = (value) => (
  Array.isArray(value) ? NOTIFICATION_EVENT_TYPES.filter((type) => value.includes(type)) : []
);

const normalizeKey = (key) => ({
  ...key,
  maxSal: key.maxSal ?? key.max_sal,
  permissions: { ...EMPTY_PERMISSIONS, ...(key.permissions || {}) },
  notificationsRead: Boolean(key.notifications_read ?? key.notificationsRead),
  notificationsWrite: Boolean(key.notifications_write ?? key.notificationsWrite),
  notificationsEventTypes: normalizeEventTypes(key.notifications_event_types ?? key.notificationsEventTypes),
});

const summarizePermissions = (permissions = {}) => {
  const enabled = PERMISSION_OPTIONS.filter(([key]) => permissions[key]).map(([, label]) => label);
  if (enabled.length === 0) return 'No permissions enabled';
  if (enabled.length <= 3) return enabled.join(', ');
  return `${enabled.slice(0, 3).join(', ')} and ${enabled.length - 3} more`;
};

const summarizeNotifications = ({ notificationsRead, notificationsWrite, notificationsEventTypes = [] }) => {
  if (!notificationsRead) return 'Notifications: read off';
  const write = notificationsWrite ? 'write on' : 'write off';
  if (notificationsEventTypes.length === 0) {
    return `Notifications: read on · ${write} · no event types (delivers nothing)`;
  }
  return `Notifications: read on · ${write} · event types: ${notificationsEventTypes.join(', ')}`;
};

const toRequestBody = (form) => ({
  label: form.label.trim() || undefined,
  max_sal: Number(form.maxSal),
  permissions: form.permissions,
  notifications_read: Boolean(form.notificationsRead),
  notifications_write: Boolean(form.notificationsWrite),
  // Filtered to the permitted set, in canonical order, and always sent as an array.
  notifications_event_types: NOTIFICATION_EVENT_TYPES.filter((type) => form.notificationsEventTypes.includes(type)),
});

const PermissionCheckboxes = ({ form, onChange, disabled, prefix }) => (
  <div className="bot-key-permission-grid">
    {PERMISSION_OPTIONS.map(([key, label]) => (
      <label key={key} className="bot-key-permission-option">
        <input
          type="checkbox"
          checked={Boolean(form.permissions[key])}
          disabled={disabled}
          onChange={(event) => onChange(key, event.target.checked)}
          data-testid={`${prefix}-permission-${key}`}
        />
        <span>{label}</span>
      </label>
    ))}
  </div>
);

const NotificationControls = ({ form, onChange, disabled, prefix }) => {
  const readOn = Boolean(form.notificationsRead);
  const pickerDisabled = disabled || !readOn;
  const toggleEventType = (type, checked) => {
    const selected = checked
      ? [...form.notificationsEventTypes, type]
      : form.notificationsEventTypes.filter((item) => item !== type);
    onChange('notificationsEventTypes', NOTIFICATION_EVENT_TYPES.filter((item) => selected.includes(item)));
  };
  return (
    <fieldset className="bot-key-notifications" data-testid={`${prefix}-notifications`}>
      <legend>Notifications for AI agents</legend>
      <div className="bot-key-permission-grid">
        <label className="bot-key-permission-option">
          <input
            type="checkbox"
            checked={readOn}
            disabled={disabled}
            onChange={(event) => onChange('notificationsRead', event.target.checked)}
            data-testid={`${prefix}-notifications-read`}
          />
          <span>Notifications read</span>
        </label>
        <label className="bot-key-permission-option">
          <input
            type="checkbox"
            checked={Boolean(form.notificationsWrite)}
            disabled={disabled}
            onChange={(event) => onChange('notificationsWrite', event.target.checked)}
            data-testid={`${prefix}-notifications-write`}
          />
          <span>Notifications write (acknowledge)</span>
        </label>
      </div>
      <fieldset
        className="bot-key-event-types"
        disabled={pickerDisabled}
        data-testid={`${prefix}-notifications-event-types`}
      >
        <legend>Event types</legend>
        {readOn ? null : <p className="form-hint">Turn on Notifications read to choose event types.</p>}
        <div className="bot-key-permission-grid">
          {NOTIFICATION_EVENT_TYPES.map((type) => (
            <label key={type} className="bot-key-permission-option">
              <input
                type="checkbox"
                checked={form.notificationsEventTypes.includes(type)}
                disabled={pickerDisabled}
                onChange={(event) => toggleEventType(type, event.target.checked)}
                data-testid={`${prefix}-event-type-${type}`}
              />
              <span>{type}</span>
            </label>
          ))}
        </div>
      </fieldset>
    </fieldset>
  );
};

const BotSessionKeysPanel = ({
  mode = 'self',
  targetUser = null,
  title = 'Static Session Keys',
  description = 'Create and manage static session keys for automation without exposing stored secrets.',
  testId = 'bot-session-keys',
}) => {
  const isAdminMode = mode === 'admin';
  const targetUserId = targetUser?.uuid || targetUser?.id || targetUser?.user_id || targetUser?.userId || '';
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [createForm, setCreateForm] = useState(emptyForm);
  const [editingKeyId, setEditingKeyId] = useState('');
  const [editForm, setEditForm] = useState(emptyForm);
  const [createdSecret, setCreatedSecret] = useState(null);

  const api = useMemo(() => ({
    list: () => (isAdminMode ? botSessionKeyApi.listForUser(targetUserId) : botSessionKeyApi.listMine()),
    create: (body) => (isAdminMode ? botSessionKeyApi.createForUser(targetUserId, body) : botSessionKeyApi.createMine(body)),
    update: (keyId, body) => (isAdminMode ? botSessionKeyApi.updateForUser(targetUserId, keyId, body) : botSessionKeyApi.updateMine(keyId, body)),
    revoke: (keyId) => (isAdminMode ? botSessionKeyApi.revokeForUser(targetUserId, keyId) : botSessionKeyApi.revokeMine(keyId)),
  }), [isAdminMode, targetUserId]);

  const canLoad = !isAdminMode || Boolean(targetUserId);

  const loadKeys = async () => {
    if (!canLoad) return;
    try {
      setLoading(true);
      setError('');
      const payload = getPayload(await api.list());
      setKeys((payload.keys || []).map(normalizeKey));
    } catch (err) {
      setKeys([]);
      setError(getErrorMessage(err, 'Failed to load static session keys.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCreatedSecret(null);
    setEditingKeyId('');
    setCreateForm(emptyForm());
    loadKeys();
  }, [mode, targetUserId]);

  const updateCreatePermission = (key, checked) => {
    setCreateForm((current) => ({
      ...current,
      permissions: { ...current.permissions, [key]: checked },
    }));
  };

  const applyProductionReadOnlyPreset = () => {
    setCreateForm((current) => ({
      ...current,
      maxSal: '3',
      permissions: { ...PRODUCTION_READ_ONLY_PERMISSIONS },
    }));
  };

  const updateEditPermission = (key, checked) => {
    setEditForm((current) => ({
      ...current,
      permissions: { ...current.permissions, [key]: checked },
    }));
  };

  const updateCreateNotification = (field, value) => {
    setCreateForm((current) => ({ ...current, [field]: value }));
  };

  const updateEditNotification = (field, value) => {
    setEditForm((current) => ({ ...current, [field]: value }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    try {
      setActionLoading(true);
      setMessage('');
      setError('');
      const body = toRequestBody(createForm);
      // The default is omitted so the server assigns the bounded default window.
      if (Number(createForm.expiryDays) !== DEFAULT_EXPIRY_DAYS) {
        body.expires_at = new Date(Date.now() + Number(createForm.expiryDays) * DAY_MS).toISOString();
      }
      const payload = getPayload(await api.create(body));
      const key = normalizeKey(payload.key || {});
      setCreatedSecret(key.sessionKey ? { keyId: key.keyId, value: key.sessionKey } : null);
      setCreateForm(emptyForm());
      setMessage('Static session key created.');
      // The raw key is returned once. Do not hold its rendering behind the
      // follow-up metadata refresh, which can be slow on a busy live API.
      // loadKeys handles its own errors, so it is safe to refresh in the
      // background after the create response has been accepted.
      void loadKeys();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create static session key.'));
    } finally {
      setActionLoading(false);
    }
  };

  const startEdit = (key) => {
    setEditingKeyId(key.keyId);
    setEditForm({
      label: key.label || '',
      maxSal: String(key.maxSal || 3),
      permissions: { ...EMPTY_PERMISSIONS, ...(key.permissions || {}) },
      notificationsRead: key.notificationsRead,
      notificationsWrite: key.notificationsWrite,
      notificationsEventTypes: [...key.notificationsEventTypes],
    });
    setMessage('');
    setError('');
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    if (!editingKeyId) return;
    try {
      setActionLoading(true);
      setMessage('');
      setError('');
      await api.update(editingKeyId, toRequestBody(editForm));
      setEditingKeyId('');
      setMessage('Static session key updated.');
      await loadKeys();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update static session key.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevoke = async (key) => {
    if (!window.confirm(`Revoke ${key.label || 'this static session key'}?`)) {
      return;
    }

    try {
      setActionLoading(true);
      setMessage('');
      setError('');
      await api.revoke(key.keyId);
      setMessage('Static session key revoked.');
      await loadKeys();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to revoke static session key.'));
    } finally {
      setActionLoading(false);
    }
  };

  const copyCreatedSecret = async () => {
    if (!createdSecret?.value || !navigator.clipboard?.writeText) return;
    await navigator.clipboard.writeText(createdSecret.value);
    setMessage('Static session key copied.');
  };

  return (
    <section className="bot-session-keys-panel" data-testid={testId}>
      <div className="bot-key-header">
        <div>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        <button type="button" className="btn btn-outline" onClick={loadKeys} disabled={loading || actionLoading || !canLoad}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {message ? <div className="message success" data-testid={`${testId}-message`}>{message}</div> : null}
      {error ? <div className="message error" data-testid={`${testId}-error`}>{error}</div> : null}

      {createdSecret ? (
        <div className="bot-key-secret" data-testid={`${testId}-created-secret`}>
          <div>
            <strong>Copy this key now</strong>
            <p>It is shown once and cannot be retrieved later.</p>
          </div>
          <textarea className="form-input" value={createdSecret.value} readOnly aria-label="New static session key" />
          <div className="bot-key-actions">
            <button type="button" className="btn btn-outline" onClick={copyCreatedSecret}>Copy</button>
            <button type="button" className="btn btn-primary" onClick={() => setCreatedSecret(null)}>
              Acknowledge secret
            </button>
          </div>
        </div>
      ) : null}

      <div className="bot-key-list" data-testid={`${testId}-list`}>
        {loading ? (
          <div className="loading-spinner">Loading static session keys...</div>
        ) : keys.length === 0 ? (
          <p className="form-hint" data-testid={`${testId}-empty`}>No static session keys have been created.</p>
        ) : (
          keys.map((key) => {
            const revoked = key.status === 'revoked';
            const isEditing = editingKeyId === key.keyId;
            const expiry = describeExpiry(key.expiresAt);
            const statusLabel = revoked ? 'revoked' : (expiry.tone === 'expired' ? 'Expired' : (key.status || 'active'));
            return (
              <article key={key.keyId} className={`bot-key-card ${revoked ? 'is-revoked' : ''}`} data-testid={`${testId}-key-card`}>
                <div className="bot-key-card-main">
                  <div>
                    <strong>{key.label || 'Unlabelled key'}</strong>
                    <span>Fingerprint {formatFingerprint(key.keyId)}</span>
                    <span>{summarizePermissions(key.permissions)}</span>
                    <span data-testid={`${testId}-key-notifications`}>{summarizeNotifications(key)}</span>
                  </div>
                  <div className="bot-key-meta">
                    <span className={`status-badge ${revoked || expiry.tone === 'expired' ? 'suspended' : 'active'}`} data-testid={`${testId}-key-status`}>{statusLabel}</span>
                    <span>SAL {key.maxSal}</span>
                  </div>
                </div>
                <dl className="bot-key-dates">
                  <div><dt>Created</dt><dd>{formatDateTime(key.createdAt)}</dd></div>
                  <div><dt>Updated</dt><dd>{formatDateTime(key.updatedAt)}</dd></div>
                  <div><dt>Last used</dt><dd>{formatDateTime(key.lastUsedAt)}</dd></div>
                  <div className={`bot-key-expiry is-${expiry.tone}`} data-tone={expiry.tone} data-testid={`${testId}-key-expiry`}>
                    <dt>Expiry</dt>
                    <dd>{expiry.text}</dd>
                  </div>
                </dl>
                {isEditing ? (
                  <form className="bot-key-form" onSubmit={handleUpdate} data-testid={`${testId}-edit-form`}>
                    <label>
                      Label
                      <input
                        className="form-input"
                        value={editForm.label}
                        onChange={(event) => setEditForm((current) => ({ ...current, label: event.target.value }))}
                        maxLength={120}
                        data-testid={`${testId}-edit-label`}
                      />
                    </label>
                    <label>
                      Max SAL
                      <select
                        value={editForm.maxSal}
                        onChange={(event) => setEditForm((current) => ({ ...current, maxSal: event.target.value }))}
                        data-testid={`${testId}-edit-max-sal`}
                      >
                        <option value="2">SAL 2</option>
                        <option value="3">SAL 3</option>
                      </select>
                    </label>
                    <PermissionCheckboxes form={editForm} onChange={updateEditPermission} disabled={actionLoading} prefix={`${testId}-edit`} />
                    <NotificationControls form={editForm} onChange={updateEditNotification} disabled={actionLoading} prefix={`${testId}-edit`} />
                    <div className="bot-key-actions">
                      <button type="submit" className="btn btn-primary" disabled={actionLoading}>Save key</button>
                      <button type="button" className="btn btn-outline" onClick={() => setEditingKeyId('')} disabled={actionLoading}>Cancel</button>
                    </div>
                  </form>
                ) : (
                  <div className="bot-key-actions">
                    <button type="button" className="btn btn-outline" onClick={() => startEdit(key)} disabled={revoked || actionLoading}>
                      Edit
                    </button>
                    <button type="button" className="btn btn-danger" onClick={() => handleRevoke(key)} disabled={revoked || actionLoading}>
                      Revoke
                    </button>
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>

      <form className="bot-key-form bot-key-create-form" onSubmit={handleCreate} data-testid={`${testId}-create-form`}>
        <h4>Create static session key</h4>
        <label>
          Label
          <input
            className="form-input"
            value={createForm.label}
            onChange={(event) => setCreateForm((current) => ({ ...current, label: event.target.value }))}
            placeholder="Production read-only bot"
            maxLength={120}
            required
            data-testid={`${testId}-create-label`}
          />
        </label>
        <label>
          Max SAL
          <select
            value={createForm.maxSal}
            onChange={(event) => setCreateForm((current) => ({ ...current, maxSal: event.target.value }))}
            data-testid={`${testId}-create-max-sal`}
          >
            <option value="2">SAL 2</option>
            <option value="3">SAL 3</option>
          </select>
        </label>
        <label>
          Expiry
          <select
            value={createForm.expiryDays}
            onChange={(event) => setCreateForm((current) => ({ ...current, expiryDays: event.target.value }))}
            data-testid={`${testId}-create-expiry`}
          >
            {EXPIRY_CHOICES.map((days) => (
              <option key={days} value={String(days)}>
                {days === DEFAULT_EXPIRY_DAYS ? `${days} days (default)` : `${days} days`}
              </option>
            ))}
          </select>
        </label>
        <PermissionCheckboxes form={createForm} onChange={updateCreatePermission} disabled={actionLoading} prefix={`${testId}-create`} />
        <NotificationControls form={createForm} onChange={updateCreateNotification} disabled={actionLoading} prefix={`${testId}-create`} />
        <button
          type="button"
          className="btn btn-outline"
          onClick={applyProductionReadOnlyPreset}
          disabled={actionLoading}
          data-testid={`${testId}-production-readonly-preset`}
        >
          Production read-only preset
        </button>
        <button type="submit" className="btn btn-primary" disabled={actionLoading || !canLoad} data-testid={`${testId}-create-submit`}>
          {actionLoading ? 'Saving...' : 'Create key'}
        </button>
      </form>
    </section>
  );
};

export default BotSessionKeysPanel;
